import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaPlay,
  FaPause,
} from "react-icons/fa";
import Slider1 from "../images/BG4.jpg";
import Slider2 from "../images/t20.jpg";
import Slider3 from "../images/t13.jpg";

/* ── Slide data ─────────────────────────────────────── */
const SLIDES = [
  {
    key:          "custom-software",
    img:          Slider3,
    eyebrow:      "Custom Engineering",
    title:        "Next-Gen Custom",
    titleAccent:  "Software Solutions",
    desc:         "Custom-built applications that integrate seamlessly with your systems or operate as standalones — designed precisely for your unique business needs.",
    cta:          { label: "Request a Demo",     to: "/service" },
    ctaSecondary: { label: "View Services",      to: "/service" },
    align:        "center",
  },
  {
    key:          "digital-transformation",
    img:          Slider2,
    eyebrow:      "Strategic Partnership",
    title:        "Driving Digital",
    titleAccent:  "Transformation",
    desc:         "Partnering with you from strategy to execution, we power your journey toward customer-focused business excellence through technology-led innovation.",
    cta:          { label: "Explore Features",   to: "/service"    },
    ctaSecondary: { label: "Our Technology",     to: "/technology" },
    align:        "left",
  },
  {
    key:          "oracle-ai",
    img:          Slider1,
    eyebrow:      "AI-Powered Solutions",
    title:        "Simplify Oracle EBS with",
    titleAccent:  "Natural Language AI",
    desc:         "Driven by people, powered by innovation, and guided by cost efficiency — query your ERP with plain language and unlock real-time enterprise intelligence.",
    cta:          { label: "Learn More",         to: "/technology" },
    ctaSecondary: { label: "Talk to an Expert",  to: "/contact"    },
    align:        "left",
  },
];

const INTERVAL = 5000;

/* ── Component ─────────────────────────────────────── */
function Sliderhome() {
  const [current,   setCurrent]   = useState(0);
  const [prev,      setPrev]      = useState(null);
  const [animating, setAnimating] = useState(false);
  const [paused,    setPaused]    = useState(false);
  const [visible,   setVisible]   = useState(false);
  const timerRef = useRef(null);

  /* entrance animation trigger */
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  /* slide transition */
  const goTo = useCallback((next) => {
    if (animating || next === current) return;
    setAnimating(true);
    setPrev(current);
    setCurrent(next);
    setTimeout(() => { setPrev(null); setAnimating(false); }, 700);
  }, [animating, current]);

  const goNext = useCallback(() => goTo((current + 1) % SLIDES.length), [goTo, current]);
  const goPrev = useCallback(() => goTo((current - 1 + SLIDES.length) % SLIDES.length), [goTo, current]);

  /* autoplay */
  useEffect(() => {
    if (paused) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(goNext, INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [paused, goNext]);

  /* keyboard navigation */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft")  goPrev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [goNext, goPrev]);

  const slide = SLIDES[current];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

        :root {
          --sh-accent:  #00c2ff;
          --sh-accent2: #7c3aed;
          --sh-overlay: rgba(5,12,30,0.78);
          --font-display: 'Syne', sans-serif;
          --font-body:    'DM Sans', sans-serif;
        }

        /* ── Shell ── */
        .sh-root {
          position: relative;
          width: 100%;
          height: 100vh;
          min-height: 540px;
          max-height: 880px;
          overflow: hidden;
          background: #05081e;
          font-family: var(--font-body);
        }

        /* ── Slide layers ── */
        .sh-slide {
          position: absolute;
          inset: 0;
          z-index: 0;
          opacity: 0;
          transition: opacity 0.7s ease;
        }
        .sh-slide.entering { opacity: 1; z-index: 1; }
        .sh-slide.leaving  { opacity: 0; z-index: 0; }

        .sh-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          transform: scale(1.06);
          transition: transform 7s ease;
        }
        .sh-slide.entering .sh-bg { transform: scale(1); }

        .sh-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            110deg,
            rgba(5,12,30,0.88) 0%,
            rgba(5,12,30,0.45) 100%
          );
        }
        .sh-vignette {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(5,12,30,0.72) 0%, transparent 45%);
        }

        /* ── Content ── */
        .sh-content-wrap {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          padding: 0 6vw;
          padding-bottom: 90px;
        }
        .sh-content-wrap.center { justify-content: center; text-align: center; }
        .sh-content-wrap.left   { justify-content: flex-start; text-align: left; }

        .sh-content {
          max-width: 680px;
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.65s ease 0.2s, transform 0.65s ease 0.2s;
        }
        .sh-content.visible { opacity: 1; transform: translateY(0); }

        /* eyebrow */
        .sh-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.74rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--sh-accent);
          background: rgba(0,194,255,0.1);
          border: 1px solid rgba(0,194,255,0.25);
          padding: 0.3rem 0.9rem;
          border-radius: 50px;
          margin-bottom: 1.1rem;
        }
        .sh-eyebrow::before {
          content: '';
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--sh-accent);
          animation: sh-pulse 1.8s infinite;
        }
        @keyframes sh-pulse {
          0%,100% { opacity:1; transform:scale(1);   }
          50%      { opacity:.4; transform:scale(.7); }
        }

        /* heading */
        .sh-title {
          font-family: var(--font-display);
          font-size: clamp(1.9rem, 4.5vw, 3.2rem);
          font-weight: 800;
          color: #fff;
          line-height: 1.1;
          margin: 0 0 0.1rem;
        }
        .sh-title-accent {
          display: block;
          font-family: var(--font-display);
          font-size: clamp(1.9rem, 4.5vw, 3.2rem);
          font-weight: 800;
          line-height: 1.1;
          margin: 0 0 1.1rem;
          background: linear-gradient(90deg, var(--sh-accent), #a78bfa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* description */
        .sh-desc {
          font-size: clamp(0.9rem, 1.4vw, 1.05rem);
          color: rgba(255,255,255,0.72);
          line-height: 1.75;
          margin-bottom: 2rem;
          max-width: 560px;
        }
        .sh-content-wrap.center .sh-desc { margin-left: auto; margin-right: auto; }

        /* CTAs */
        .sh-ctas {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: center;
        }
        .sh-content-wrap.center .sh-ctas { justify-content: center; }

        .sh-btn-primary {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 0.7rem 1.5rem;
          background: var(--sh-accent);
          color: #040e1e;
          font-family: var(--font-body);
          font-size: 0.9rem; font-weight: 700;
          border-radius: 8px; text-decoration: none;
          transition: opacity 0.2s, box-shadow 0.25s, transform 0.2s;
        }
        .sh-btn-primary:hover {
          opacity: 0.88; color: #040e1e;
          box-shadow: 0 0 22px rgba(0,194,255,0.45);
          transform: translateY(-2px);
        }
        .sh-btn-primary svg { font-size: 0.75rem; transition: transform 0.2s; }
        .sh-btn-primary:hover svg { transform: translateX(3px); }

        .sh-btn-ghost {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 0.68rem 1.4rem;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.22);
          color: rgba(255,255,255,0.88);
          font-family: var(--font-body);
          font-size: 0.9rem; font-weight: 600;
          border-radius: 8px; text-decoration: none;
          backdrop-filter: blur(6px);
          transition: background 0.2s, border-color 0.2s, color 0.2s, transform 0.2s;
        }
        .sh-btn-ghost:hover {
          background: rgba(255,255,255,0.15);
          border-color: rgba(255,255,255,0.4);
          color: #fff; transform: translateY(-2px);
        }

        /* ── Bottom bar ── */
        .sh-bottom {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          z-index: 3;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.25rem 6vw;
          background: linear-gradient(to top, rgba(5,12,30,0.65), transparent);
        }

        /* counter + dots */
        .sh-left-bar { display: flex; align-items: center; gap: 1rem; }

        .sh-counter {
          font-size: 0.78rem; font-weight: 600;
          color: rgba(255,255,255,0.5);
          letter-spacing: 0.06em; min-width: 46px;
        }
        .sh-counter span { color: #fff; }

        .sh-dots { display: flex; align-items: center; gap: 0.5rem; }
        .sh-dot {
          position: relative;
          height: 3px; border-radius: 3px;
          background: rgba(255,255,255,0.22);
          overflow: hidden; cursor: pointer;
          transition: width 0.3s ease, background 0.3s;
          border: none; padding: 0;
        }
        .sh-dot.inactive { width: 20px; }
        .sh-dot.active   { width: 52px; background: rgba(255,255,255,0.3); }
        .sh-dot-fill {
          position: absolute; inset: 0;
          background: var(--sh-accent);
          transform: scaleX(0); transform-origin: left;
        }
        .sh-dot.active:not(.paused) .sh-dot-fill {
          animation: sh-fill linear var(--sh-interval, 5000ms) forwards;
        }
        @keyframes sh-fill { from { transform: scaleX(0); } to { transform: scaleX(1); } }

        /* controls */
        .sh-controls { display: flex; align-items: center; gap: 0.5rem; }
        .sh-ctrl {
          width: 38px; height: 38px; border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.18);
          background: rgba(255,255,255,0.07);
          color: rgba(255,255,255,0.75); font-size: 0.75rem;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; backdrop-filter: blur(6px);
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .sh-ctrl:hover {
          background: rgba(255,255,255,0.16); color: #fff;
          border-color: rgba(255,255,255,0.35);
        }
        .sh-pause {
          width: 38px; height: 38px; border-radius: 8px;
          border: 1px solid rgba(0,194,255,0.3);
          background: rgba(0,194,255,0.1);
          color: var(--sh-accent); font-size: 0.7rem;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; margin-left: 0.25rem;
          transition: background 0.2s;
        }
        .sh-pause:hover { background: rgba(0,194,255,0.2); }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .sh-root        { height: 85vh; min-height: 500px; }
          .sh-content-wrap{ padding: 0 1.5rem; padding-bottom: 100px; }
          .sh-bottom      { padding: 1rem 1.5rem; }
          .sh-desc        { font-size: 0.9rem; }
          .sh-btn-primary,
          .sh-btn-ghost   { font-size: 0.82rem; padding: 0.6rem 1.1rem; }
        }
        @media (max-width: 480px) {
          .sh-root        { height: 90vh; }
          .sh-ctas        { flex-direction: column; align-items: flex-start; }
          .sh-content-wrap.center .sh-ctas { align-items: center; }
          .sh-title,
          .sh-title-accent{ font-size: 1.75rem; }
        }
      `}</style>

      <section
        className="sh-root"
        aria-label="Hero carousel"
        aria-roledescription="carousel"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* ── Background slide layers ── */}
        {SLIDES.map((s, i) => (
          <div
            key={s.key}
            className={`sh-slide ${
              i === current ? "entering" : i === prev ? "leaving" : ""
            }`}
            aria-hidden={i !== current}
          >
            <div
              className="sh-bg"
              style={{ backgroundImage: `url(${s.img})` }}
              role="img"
              aria-label={`${s.eyebrow} background`}
            />
            <div className="sh-overlay" />
            <div className="sh-vignette" />
          </div>
        ))}

        {/* ── Active slide content ── */}
        <div
          className={`sh-content-wrap ${slide.align}`}
          aria-live="polite"
        >
          <div className={`sh-content ${visible ? "visible" : ""}`} key={slide.key}>
            <div className="sh-eyebrow">{slide.eyebrow}</div>
            <h1 className="sh-title">{slide.title}</h1>
            <span className="sh-title-accent">{slide.titleAccent}</span>
            <p className="sh-desc">{slide.desc}</p>
            <div className="sh-ctas">
              {/* <Link to={slide.cta.to} className="sh-btn-primary">
                {slide.cta.label} <FaArrowRight />
              </Link> */}
              <Link to={slide.ctaSecondary.to} className="sh-btn-ghost">
                {slide.ctaSecondary.label}
              </Link>
            </div>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="sh-bottom">
          <div className="sh-left-bar">
            <div className="sh-counter">
              <span>{String(current + 1).padStart(2, "0")}</span>
              {" / "}
              {String(SLIDES.length).padStart(2, "0")}
            </div>
            <div className="sh-dots" role="tablist" aria-label="Slide navigation">
              {SLIDES.map((s, i) => (
                <button
                  key={s.key}
                  className={`sh-dot ${i === current ? "active" : "inactive"} ${
                    paused ? "paused" : ""
                  }`}
                  style={{ "--sh-interval": `${INTERVAL}ms` }}
                  onClick={() => goTo(i)}
                  role="tab"
                  aria-selected={i === current}
                  aria-label={`Go to slide ${i + 1}: ${s.eyebrow}`}
                >
                  <span className="sh-dot-fill" />
                </button>
              ))}
            </div>
          </div>

          <div className="sh-controls">
            <button className="sh-ctrl" onClick={goPrev} aria-label="Previous slide">
              <FaChevronLeft />
            </button>
            <button
              className="sh-pause"
              onClick={() => setPaused((v) => !v)}
              aria-label={paused ? "Resume autoplay" : "Pause autoplay"}
              aria-pressed={paused}
            >
              {paused ? <FaPlay /> : <FaPause />}
            </button>
            <button className="sh-ctrl" onClick={goNext} aria-label="Next slide">
              <FaChevronRight />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

export default Sliderhome;